import { Text } from 'designSystem/components/Text'
import { FavoriteSearch } from 'features/knowledgeDocumentViewer/utils/favoriteSearchesStorage'
import { SearchQueryHistoryEntry } from 'features/knowledgeDocumentViewer/utils/searchQueryHistoryStorage'
import { Clock, Star, X } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 70;
  margin-top: 0.25rem;
  background: ${(props) => props.theme.forecast.background2};
  border: 1px solid ${(props) => props.theme.forecast.border};
  border-radius: 0.375rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 20rem;
  overflow-y: auto;
`

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem 0.25rem;
`

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6875rem;
  color: ${(props) => props.theme.forecast.tertiaryText};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;

  &:hover {
    color: ${(props) => props.theme.forecast.defaultText};
    background: ${(props) => props.theme.forecast.hoverGhostButton};
  }
`

const SuggestionItem = styled.button<{ $isHighlighted: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: ${(props) =>
    props.$isHighlighted
      ? props.theme.forecast.hoverGhostButton
      : 'transparent'};
  border: none;
  cursor: pointer;
  text-align: left;
  color: ${(props) => props.theme.forecast.defaultText};
  font-size: 0.8125rem;

  &:hover {
    background: ${(props) => props.theme.forecast.hoverGhostButton};
  }
`

const SuggestionIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.forecast.tertiaryText};
  flex-shrink: 0;
`

const ClickableSuggestionIcon = styled.button`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  color: ${(props) => props.theme.forecast.defaultText};
  border-radius: 0.25rem;

  &:hover {
    color: ${(props) => props.theme.forecast.tertiaryText};
  }
`

const SuggestionText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PinButton = styled.button<{ $isPinned: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  border-radius: 0.25rem;
  color: ${(props) => props.theme.forecast.defaultText};
  opacity: ${(props) => (props.$isPinned ? 1 : 0)};
  transition: opacity 120ms ease-out;

  ${SuggestionItem}:hover & {
    opacity: 1;
  }

  &:hover {
    color: ${(props) => props.theme.forecast.defaultText};
  }
`

const SectionDivider = styled.div`
  height: 1px;
  background: ${(props) => props.theme.forecast.border};
  margin: 0.25rem 0.75rem;
`

const EmptyMessage = styled.div`
  padding: 0.75rem;
  text-align: center;
  font-size: 0.8125rem;
  color: ${(props) => props.theme.forecast.tertiaryText};
  font-style: italic;
`

export type SuggestionRow = {
  query: string
  isPinned: boolean
  timestamp: number
}

interface SearchSuggestionsDropdownProps {
  suggestions: SearchQueryHistoryEntry[]
  favoriteSearches?: FavoriteSearch[]
  isSearchFavorited?: (query: string) => boolean
  onToggleSearchFavorite?: (query: string, contentFilter: string) => void
  contentFilter?: string
  onSelect: (query: string) => void
  onClearHistory: () => void
  highlightedIndex: number
}

export const SearchSuggestionsDropdown: React.FC<
  SearchSuggestionsDropdownProps
> = ({
  suggestions,
  favoriteSearches,
  isSearchFavorited,
  onToggleSearchFavorite,
  contentFilter,
  onSelect,
  onClearHistory,
  highlightedIndex,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map())

  const rows = useMemo<SuggestionRow[]>(() => {
    const pinnedQueries = new Set(
      (favoriteSearches ?? []).map((f) => f.query.toLowerCase())
    )

    const pinned: SuggestionRow[] = (favoriteSearches ?? []).map((f) => ({
      query: f.query,
      isPinned: true,
      timestamp: f.timestamp,
    }))

    const recent: SuggestionRow[] = suggestions
      .filter((s) => !pinnedQueries.has(s.query.toLowerCase()))
      .map((s) => ({
        query: s.query,
        isPinned: false,
        timestamp: s.timestamp,
      }))

    return [...pinned, ...recent]
  }, [suggestions, favoriteSearches])

  const pinnedCount = useMemo(
    () => rows.filter((r) => r.isPinned).length,
    [rows]
  )

  useEffect(() => {
    const el = itemRefs.current.get(highlightedIndex)
    el?.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex])

  const setItemRef = useCallback(
    (index: number) => (el: HTMLButtonElement | null) => {
      if (el) {
        itemRefs.current.set(index, el)
      } else {
        itemRefs.current.delete(index)
      }
    },
    []
  )

  if (rows.length === 0) {
    return (
      <DropdownContainer ref={containerRef}>
        <EmptyMessage>No recent searches</EmptyMessage>
      </DropdownContainer>
    )
  }

  return (
    <DropdownContainer ref={containerRef}>
      <DropdownHeader>
        <Text variant='overline' weight='normal' color='tertiary'>
          {pinnedCount > 0 ? 'Saved & recent searches' : 'Recent searches'}
        </Text>
        <ClearButton onClick={onClearHistory}>
          <X size={11} />
          Clear recents
        </ClearButton>
      </DropdownHeader>
      {rows.map((row, index) => (
        <React.Fragment key={`${row.query}-${row.isPinned}`}>
          {index === pinnedCount && pinnedCount > 0 && <SectionDivider />}
          <SuggestionItem
            ref={setItemRef(index)}
            $isHighlighted={index === highlightedIndex}
            onClick={() => onSelect(row.query)}
            onMouseDown={(e) => e.preventDefault()}>
            {row.isPinned && onToggleSearchFavorite && contentFilter ? (
              <ClickableSuggestionIcon
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleSearchFavorite(row.query, contentFilter)
                }}
                onMouseDown={(e) => e.preventDefault()}
                title='Remove from saved'>
                <Star size={14} fill='currentColor' />
              </ClickableSuggestionIcon>
            ) : (
              <SuggestionIcon>
                <Clock size={14} />
              </SuggestionIcon>
            )}
            <SuggestionText>{row.query}</SuggestionText>
            {!row.isPinned && onToggleSearchFavorite && contentFilter && (
              <PinButton
                $isPinned={isSearchFavorited?.(row.query) ?? false}
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleSearchFavorite(row.query, contentFilter)
                }}
                onMouseDown={(e) => e.preventDefault()}
                title='Save this search'>
                <Star
                  size={12}
                  fill={
                    isSearchFavorited?.(row.query) ? 'currentColor' : 'none'
                  }
                />
              </PinButton>
            )}
          </SuggestionItem>
        </React.Fragment>
      ))}
    </DropdownContainer>
  )
}
